import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

const ViewResponseModal = ({ isOpen, onClose, data }) => {
  const [copied, setCopied] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle smooth enter/exit animations
  useEffect(() => {
    if (isOpen) {
      // Small delay to allow the DOM node to mount before starting the slide transition
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300); // Wait for slide-out animation to finish before unmounting
  };

  const handleCopy = () => {
    if (!data?.response) return;
    navigator.clipboard.writeText(data.response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm font-outfit transition-opacity duration-300 ease-out ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
      onClick={handleClose}
    >
      {/* Right Slide-over Drawer (Fixed position, strictly tied to right edge) */}
      <div 
        className={`fixed top-0 right-0 h-full bg-white w-[600px] max-w-[90vw] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8E8E8] shrink-0 bg-white">
          <div className="flex items-center gap-2">
            <h2 className="text-[20px] font-medium text-[#BB42AE]">Response Details</h2>
            <span className="text-[#E3E3E3] font-light">|</span>
            <span className="text-[16px] text-[#000000B3] font-medium">{data?.timestamp || "-"}</span>
          </div>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-md hover:bg-gray-50"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar flex flex-col gap-6">
          
          {/* User Query Section */}
          <div className="flex flex-col gap-0 shrink-0">
            <label className="text-[16px] font-medium text-[#000000B3]">User Query</label>
            <div className="p-2 border border-[#E3E3E3] rounded-lg bg-[#97979712] text-[14px] text-[#000000B3] leading-relaxed">
              {data?.user_query || "-"}
            </div>
          </div>

          {/* AI Response Section */}
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-center justify-between">
              <label className="text-[16px] font-medium text-[#000000B3]">AI Response</label>
              <button 
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-[13px] font-medium text-[#BB42AE] hover:underline"
              >
                {copied ? "Copied!" : "Copy Response"}
                {!copied && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
              </button>
            </div>
            
            <div className="p-2 bg-white border border-[#E3E3E3] rounded-lg text-[14px] text-[#000000B3] leading-relaxed whitespace-pre-wrap min-h-[150px]">
              {data?.response || "-"}
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewResponseModal;